// ============================================================
// fondos/texturas.js — LAS 5 TEXTURAS CSS: como fondo y como overlay (DOM: solo escribe estilos)
//
// Una textura no se mueve: es un `background` hecho de degradados con el color de acento. Sirve
// de FONDO de una sección (lo pinta el motor en su capa `.bg-css-layer`, `fondos/capa.js`) y de
// OVERLAY combinado sobre un fondo animado (`.bg-overlay`). Vivían como dos registros casi
// gemelos dentro de `animations/backgrounds.js` (`BackgroundRegistry` y `OverlayRegistry`).
//
// Los dos registros solo difieren en `radial-glow`: como overlay, más brillo (ver ALFA_OVERLAY) y
// hasta el 65 % del radio en vez del 60 %. Unificarlos cambiaría 31 bloques de test222 sin arreglar
// nada (plan del P3 §9).
//
// Qué texturas hay lo dice el CATÁLOGO (`fondos/catalogo.js`, `categoria: 'textura'`);
// `tests/test_cobertura_visual.py` cruza estos dos registros con él.
// ============================================================

/**
 * INTENSIDAD: el alfa (%) del acento de cada textura a opacidad 100, según la polaridad de la sección
 * (decisión del dueño, 01-10-2026: «aunque estén al 100 % se ven poco»). Hasta entonces era 8/25 mesh,
 * 5/15 grid, 6/20 glow, 4/10 diagonal y 6/15 puntos (claro/oscuro): al 100 % la marca se apartaba
 * 7-13 niveles del fondo liso en claro, y con lo que guarda la IA (20-60 %), 3-8: no se veía. Es la
 * opción «B · visible» del arnés (`CLAUDE_RESUMEN_PLANES/2026-10-01_produccion-adjuntos/texturas/`):
 * 24-56 niveles al 100 %, y la legibilidad de la F3 (titular ≥ 3, párrafo ≥ 4,5, anillo del botón
 * ≥ 2,5) en claro, pastel y oscuro y en 18 bloques reales de test222. Las finas (rejilla, diagonales,
 * puntos) no mueven el contraste del texto; los puntos llevan más alfa porque ocupan el 0,7 % del área.
 * La opacidad GUARDADA se pinta además por la curva del catálogo (`catalogo.js::curvaOpacidad`).
 */
const ALFA = {
    'gradient-mesh': { claro: 28, oscuro: 50 },
    'grid': { claro: 20, oscuro: 40 },
    'radial-glow': { claro: 32, oscuro: 50 },
    'diagonal-lines': { claro: 20, oscuro: 35 },
    'dot-matrix': { claro: 50, oscuro: 75 },
};

/** Como OVERLAY, la misma proporción que tenía con su fondo: solo el brillo sube (antes 10/35 frente a 6/20). */
const ALFA_OVERLAY = Object.assign({}, ALFA, { 'radial-glow': { claro: 53, oscuro: 88 } });

function alfa(tabla, id, isDark) {
    return isDark ? tabla[id].oscuro : tabla[id].claro;
}

/**
 * El alfa (0-100) con que pinta su color la textura `id` a opacidad 100, como fondo o como overlay;
 * 0 si no es una textura. Lo usa el tope de legibilidad (`fondos/efectiva.js` + `fondos/legible.js`).
 */
export function alfaDeTextura(id, isDark, comoOverlay) {
    const tabla = comoOverlay ? ALFA_OVERLAY : ALFA;
    return _tiene(tabla, id) ? alfa(tabla, id, isDark) : 0;
}

/** Las texturas como FONDO de la sección. Pintan en el contenedor que reciben (su capa propia). */
export const TEXTURAS_DE_FONDO = {
    'gradient-mesh': (container, accent, isDark) => {
        const op = alfa(ALFA, 'gradient-mesh', isDark);
        container.style.background = `
            radial-gradient(circle at 15% 15%, color-mix(in srgb, ${accent} ${op}%, transparent) 0%, transparent 50%),
            radial-gradient(circle at 85% 20%, color-mix(in srgb, ${accent} ${op}%, transparent) 0%, transparent 40%),
            radial-gradient(circle at 50% 85%, color-mix(in srgb, ${accent} ${op}%, transparent) 0%, transparent 55%)
        `;
    },
    'grid': (container, accent, isDark) => {
        const op = alfa(ALFA, 'grid', isDark);
        const color = `color-mix(in srgb, ${accent} ${op}%, transparent)`;
        container.style.backgroundImage = `
            linear-gradient(to right, ${color} 1px, transparent 1px),
            linear-gradient(to bottom, ${color} 1px, transparent 1px)
        `;
        container.style.backgroundSize = '40px 40px';
    },
    'radial-glow': (container, accent, isDark) => {
        const op = alfa(ALFA, 'radial-glow', isDark);
        container.style.background = `radial-gradient(circle at 50% 50%, color-mix(in srgb, ${accent} ${op}%, transparent) 0%, transparent 60%)`;
    },
    'diagonal-lines': (container, accent, isDark) => {
        const op = alfa(ALFA, 'diagonal-lines', isDark);
        const color = `color-mix(in srgb, ${accent} ${op}%, transparent)`;
        container.style.backgroundImage = `repeating-linear-gradient(45deg, ${color} 0, ${color} 1px, transparent 1px, transparent 20px)`;
    },
    'dot-matrix': (container, accent, isDark) => {
        const op = alfa(ALFA, 'dot-matrix', isDark);
        const color = `color-mix(in srgb, ${accent} ${op}%, transparent)`;
        container.style.backgroundImage = `radial-gradient(${color} 1.5px, transparent 1.5px)`;
        container.style.backgroundSize = '24px 24px';
    },
};

/** Las texturas como OVERLAY combinado sobre un fondo animado (`div.bg-overlay`, `fondos/capa.js`). */
export const TEXTURAS_DE_OVERLAY = {
    'gradient-mesh': (el, accent, isDark) => {
        const op = alfa(ALFA_OVERLAY, 'gradient-mesh', isDark);
        el.style.background = `
            radial-gradient(circle at 15% 15%, color-mix(in srgb, ${accent} ${op}%, transparent) 0%, transparent 50%),
            radial-gradient(circle at 85% 20%, color-mix(in srgb, ${accent} ${op}%, transparent) 0%, transparent 40%),
            radial-gradient(circle at 50% 85%, color-mix(in srgb, ${accent} ${op}%, transparent) 0%, transparent 55%)
        `;
    },
    'grid': (el, accent, isDark) => {
        const op = alfa(ALFA_OVERLAY, 'grid', isDark);
        const color = `color-mix(in srgb, ${accent} ${op}%, transparent)`;
        el.style.backgroundImage = `linear-gradient(to right, ${color} 1px, transparent 1px), linear-gradient(to bottom, ${color} 1px, transparent 1px)`;
        el.style.backgroundSize = '40px 40px';
    },
    'radial-glow': (el, accent, isDark) => {
        const op = alfa(ALFA_OVERLAY, 'radial-glow', isDark);
        el.style.background = `radial-gradient(circle at 50% 50%, color-mix(in srgb, ${accent} ${op}%, transparent) 0%, transparent 65%)`;
    },
    'diagonal-lines': (el, accent, isDark) => {
        const op = alfa(ALFA_OVERLAY, 'diagonal-lines', isDark);
        const color = `color-mix(in srgb, ${accent} ${op}%, transparent)`;
        el.style.backgroundImage = `repeating-linear-gradient(45deg, ${color} 0, ${color} 1px, transparent 1px, transparent 20px)`;
    },
    'dot-matrix': (el, accent, isDark) => {
        const op = alfa(ALFA_OVERLAY, 'dot-matrix', isDark);
        const color = `color-mix(in srgb, ${accent} ${op}%, transparent)`;
        el.style.backgroundImage = `radial-gradient(${color} 1.5px, transparent 1.5px)`;
        el.style.backgroundSize = '24px 24px';
    },
};

// `hasOwnProperty` y no `REGISTRO[id]`: un id guardado como 'constructor' o 'toString' casaría con
// lo que el objeto hereda de Object.prototype (y con él se llegó a abortar el bucle de la página).
function _tiene(registro, id) {
    return typeof id === 'string' && Object.prototype.hasOwnProperty.call(registro, id);
}

/** ¿`id` es una textura que se sabe pintar como fondo? */
export function esTexturaDeFondo(id) {
    return _tiene(TEXTURAS_DE_FONDO, id);
}

/** ¿`id` es un overlay que se sabe pintar? (la normalización del overlay, `fondos/efectiva.js`) */
export function esTexturaDeOverlay(id) {
    return _tiene(TEXTURAS_DE_OVERLAY, id);
}

/** Pinta la textura `id` como fondo en `container`. @returns {boolean} si la conocía. */
export function pintarTexturaDeFondo(container, id, accent, isDark) {
    if (!esTexturaDeFondo(id) || !container) return false;
    TEXTURAS_DE_FONDO[id](container, accent, isDark);
    return true;
}

/** Pinta el overlay `id` en `el` (el `.bg-overlay` ya creado). @returns {boolean} si lo conocía. */
export function pintarTexturaDeOverlay(el, id, accent, isDark) {
    if (!esTexturaDeOverlay(id) || !el) return false;
    TEXTURAS_DE_OVERLAY[id](el, accent, isDark);
    return true;
}
